import { Button, type IButtonProps, type IIconProps } from '@ad-lucy/ui';
import type { StyledBadgeProps } from '@ad-lucy/ui/lib/components/Navbar/components/Item/components/Link';
import type { StyledDotProps } from '@modules/Layout/Default/Sidebar/Navbar/types';
import type { ElementType, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styled, { type keyframes } from 'styled-components';

type Keyframes = ReturnType<typeof keyframes>;

type TLinkBaseProps<TAsComponent extends ElementType> = {
  asComponent?: TAsComponent;
  icon?: IIconProps['type'];
  badge?: StyledBadgeProps;
  dot?: StyledDotProps;
  text?: string;
  title?: string;
  active?: boolean;
  collapsed?: boolean;
  animation?: Keyframes;
};

type IStyledLinkProps = Omit<TLinkBaseProps<'a'>, 'active'> & {
  $active: boolean;
  $dot?: StyledDotProps;
  $collapsed?: boolean;
  to: string;
  children?: ReactNode;
};

const StyledButton = styled(Button)`
  user-select: none;
`;

const StyledLink = styled(({ icon, ...props }: IStyledLinkProps) => (
  <Link {...props} />
))`
  text-decoration: none;
`;

// React Router doesn't allow rendering links with no target url,
// so to render disabled link we render the original Button component when disabled.
type EnabledProps = IButtonProps<typeof Link> & {
  disabled?: false;
};

type DisabledProps = IButtonProps &
  LinkProps & {
    disabled: true;
  };

type LinkButtonProps = EnabledProps | DisabledProps;

export function LinkButton(props: Readonly<LinkButtonProps>) {
  const { disabled } = props;
  if (disabled) {
    return <StyledButton {...props} />;
  }
  return <StyledButton asComponent={StyledLink} {...props} />;
}
